import { useEffect, useState, useRef, useCallback } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { ScrollSpy } from "../../../setup/util/scrollSpy";

interface Props {
  tabs: { title: string | undefined; hash: string }[];
  tabHandler?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const Tabs = ({ tabs, tabHandler }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState<{
    title: string | undefined;
    hash: string;
  }>();

  const onTabClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    tabIndex: number
  ) => {
    setSelectedTab(tabs[tabIndex]);
    tabHandler?.(e);
  };

  const onScrollUpdate = useCallback(
    (entry: IntersectionObserverEntry, isInVewPort: boolean) => {
      const { target, boundingClientRect } = entry;
      const menuItems = document.querySelectorAll(
        '[data-scrollspy-id^="section"]'
      );
      const tabIndex = Array.from(menuItems).findIndex((item) => {
        return item?.id == target?.id;
      });

      if (
        boundingClientRect.y <= 0 &&
        isInVewPort &&
        tabIndex !== -1 &&
        tabs[tabIndex]
      ) {
        setSelectedTab(tabs[tabIndex]);
      }
    },
    [tabs]
  );

  useEffect(() => {
    if (!selectedTab) {
      setSelectedTab(tabs[0]);
    }
  }, [selectedTab, tabs]);

  useEffect(() => {
    const onScroll = () => {
      const header = containerRef?.current;
      const stickyHeight = header?.offsetTop;

      if (
        stickyHeight &&
        window.scrollY > stickyHeight &&
        !header?.classList?.contains(styles["sticky_tabs"])
      ) {
        header?.classList.add(styles["sticky_tabs"]);
        // original sticky height location
      } else if (window.scrollY <= 453) {
        header?.classList.remove(styles["sticky_tabs"]);
      }
    };
    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div>
      <ScrollSpy handleScroll={onScrollUpdate} />

      <div ref={containerRef} className={styles.tabs_container}>
        {tabs?.map((tab, index) => (
          <a
            key={tab?.title}
            href={`#${tab?.hash}`}
            onClick={(e) => onTabClick(e, index)}
            data-scrollspy-id={tab?.hash}
            id={tab?.hash}
            className={classNames(styles["tab"], {
              [styles["selected_tab"]]: selectedTab?.title === tab?.title,
            })}>
            {tab?.title}
          </a>
        ))}
      </div>
    </div>
  );
};

export default Tabs;
